import { useCallback, useEffect, useRef, useState } from 'react'
import type { VisitWriteRequest } from '@/api/types'
import { discardDraft, draftHasContent, loadDraft, saveDraft, type VisitDraft } from './drafts'

const DEBOUNCE_MS = 1000

interface Options {
  userId: string | null
  patientId: string
  visitId: string
  enabled?: boolean
}

/** Persists what is being typed on a ~1s debounce: one second of loss is not
 *  the failure anyone is worried about. Losing three minutes is. */
export function useVisitDraft({ userId, patientId, visitId, enabled = true }: Options) {
  const [restored, setRestored] = useState<VisitDraft | null>(null)
  const [savedAt, setSavedAt] = useState<string | null>(null)
  const [checked, setChecked] = useState(false)
  const timer = useRef<number | null>(null)

  useEffect(() => {
    if (!enabled || !userId) {
      setChecked(true)
      return
    }
    let cancelled = false
    void loadDraft(userId, patientId, visitId).then((draft) => {
      if (cancelled) return
      if (draft && draftHasContent(draft.content)) setRestored(draft)
      setChecked(true)
    })
    return () => {
      cancelled = true
    }
  }, [userId, patientId, visitId, enabled])

  const persist = useCallback(
    (content: VisitWriteRequest) => {
      if (!enabled || !userId) return
      if (timer.current !== null) window.clearTimeout(timer.current)
      timer.current = window.setTimeout(() => {
        void saveDraft({ userId, patientId, visitId, content }).then((draft) =>
          setSavedAt(draft.savedAt),
        )
      }, DEBOUNCE_MS)
    },
    [enabled, userId, patientId, visitId],
  )

  /** Flush immediately — used when the tab is hidden, which is the moment a
   *  debounced write would otherwise be lost. */
  const flush = useCallback(
    async (content: VisitWriteRequest) => {
      if (!enabled || !userId) return
      if (timer.current !== null) window.clearTimeout(timer.current)
      if (!draftHasContent(content)) return
      const draft = await saveDraft({ userId, patientId, visitId, content })
      setSavedAt(draft.savedAt)
    },
    [enabled, userId, patientId, visitId],
  )

  const clear = useCallback(async () => {
    if (!userId) return
    if (timer.current !== null) window.clearTimeout(timer.current)
    await discardDraft(userId, patientId, visitId)
    setSavedAt(null)
    setRestored(null)
  }, [userId, patientId, visitId])

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current)
    },
    [],
  )

  return { restored, dismissRestored: () => setRestored(null), savedAt, checked, persist, flush, clear }
}
